import React, {useMemo, useState} from 'react';
import styles from './_finance-irregular-payment-stream-editor-dialog.module.scss';
import {Button, DialogActions, DialogContent, DialogTitle, Grid} from "@mui/material";
import {Error, Hint} from "@progress/kendo-react-labels";
import {
  FinanceQuote,
  FinanceQuoteIrregularPaymentStream,
  hasValue,
  NoScrollNumericTextBox,
  SolveFor
} from "@amsanalytics/react-shared-library";
import {ComboBox, ComboBoxChangeEvent} from "@progress/kendo-react-dropdowns";
import {Input, InputChangeEvent, NumericTextBoxChangeEvent} from "@progress/kendo-react-inputs";
import {ILookupItems} from "../../../../../Interfaces/ILookupItems";
import {DialogProps} from "../../../../Dialogs/DialogProps";
import {
  advanceDateByPaymentPeriods,
  calculatePossiblePaymentDates, estimateTermFromPayment,
  IrregularPaymentStartDateOption
} from '../../../../../utils/quoteUtils';

export interface IGenericItemEditorError {
  message: string;
  canBeBypassed: boolean;
}

type SubmitButtonText = "Add" | "Update";
export type GenericEditorActionType = SubmitButtonText | "Bypass";

export interface FinanceIrregularPaymentStreamEditorDialogProps extends DialogProps<void> {
  selectedFinanceQuote: FinanceQuote;
  theoreticalAmountToFinance: number;
  irregularPaymentStream: FinanceQuoteIrregularPaymentStream;
  submitButtonText: SubmitButtonText;
  onSubmit: (item: FinanceQuoteIrregularPaymentStream) => void;
  onRemoveSingle: (irregularPaymentId: string) => void;
  lookups: ILookupItems;
  maxNumberOfPaymentStreams: number;
}

// Much of this was copied from the GenericItemEditorDialog
export default function FinanceIrregularPaymentStreamEditorDialog(props: FinanceIrregularPaymentStreamEditorDialogProps) {
  const financeQuoteIrregularPaymentStreams = props.selectedFinanceQuote.financeQuoteIrregularPaymentStreams;
  const thereWasAtLeastOneIrregularPaymentOnOpen = financeQuoteIrregularPaymentStreams.length > 0;
  const [irregularPaymentStream, setIrregularPaymentStream] = useState<FinanceQuoteIrregularPaymentStream>(props.irregularPaymentStream);
  const [validatorErrors, setValidatorErrors] = useState<IGenericItemEditorError[]>([]);
  const errorsThatCanBeBypassed = validatorErrors.filter(e => e.canBeBypassed);
  const errorsThatCanNotBeBypassed = validatorErrors.filter(e => !e.canBeBypassed);
  let numberOfPaymentsToUse: number | undefined;

  // Calculate possible payment dates based on the quote's first payment date and payment frequency
  const paymentDateOptions = useMemo(() => {
    if (props.selectedFinanceQuote.solveFor == SolveFor.Term) {
      const estimatedTerm = estimateTermFromPayment(
        props.selectedFinanceQuote.payment ?? 0, 
        props.theoreticalAmountToFinance, 
        props.selectedFinanceQuote.rate,
        props.selectedFinanceQuote.paymentFrequency
      );
      numberOfPaymentsToUse = (estimatedTerm.numberOfPayments ?? 0) + 2;
    } else {
      // If the quote is being solved for payment, the term is already defined
      numberOfPaymentsToUse = props.selectedFinanceQuote.numberOfPayments;
    }
        
    const allDates = calculatePossiblePaymentDates(
      props.selectedFinanceQuote.firstPaymentDate,
      props.selectedFinanceQuote.paymentFrequency,
      numberOfPaymentsToUse
    );

    // Get all other irregular payment streams (excluding the one being edited)
    const otherPaymentStreams = financeQuoteIrregularPaymentStreams.filter(
      stream => stream.id !== irregularPaymentStream.id
    );

    // Filter out dates that fall within existing payment stream ranges
    return allDates.filter(dateOption => {
      // Create a new Date to avoid mutating the original
      const dateTime = new Date(dateOption.date).setHours(0, 0, 0, 0);
      
      for (const existingStream of otherPaymentStreams) {
        if (!existingStream.startDate || !existingStream.endDate) continue;
        
        const streamStart = new Date(existingStream.startDate).setHours(0, 0, 0, 0);
        const streamEnd = new Date(existingStream.endDate).setHours(0, 0, 0, 0);
        
        // If this date falls within an existing stream's range, filter it out
        if (dateTime >= streamStart && dateTime <= streamEnd) {
          return false;
        }
      }
      return true;
    });
  }, [
    props.selectedFinanceQuote.firstPaymentDate, 
    props.selectedFinanceQuote.paymentFrequency,
    financeQuoteIrregularPaymentStreams,
    irregularPaymentStream.id
  ]);

  // Find the currently selected payment date option
  const selectedPaymentDateOption = useMemo(() => {
    if (!irregularPaymentStream.startDate) return undefined;
    const startDateTimestamp = new Date(irregularPaymentStream.startDate).getTime();
    return paymentDateOptions.find(option => new Date(option.date).getTime() === startDateTimestamp);
  }, [irregularPaymentStream.startDate, paymentDateOptions]);

  /**
   * Calculates the end date based on start date, payment frequency, and number of payments.
   * The end date is the date of the last payment in the stream.
   */
  const calculateEndDate = (startDate: Date | null, count: number): Date | null => {
    if (!startDate || count <= 0) return null;
    
    const paymentFrequency = props.selectedFinanceQuote.paymentFrequency;
    const numberOfPeriodsToAdd = count - 1; // -1 because start date is the first payment

    if (numberOfPeriodsToAdd === 0) return new Date(startDate);

    return advanceDateByPaymentPeriods(startDate, paymentFrequency, numberOfPeriodsToAdd);
  };

  /**
   * Handler for when the start date is changed.
   * Updates both the start date and recalculates the end date.
   */
  const handleStartDateChange = (selectedOption: IrregularPaymentStartDateOption | null) => {
    const newStartDate = selectedOption?.date ?? null;
    const newEndDate = calculateEndDate(newStartDate, irregularPaymentStream.count);
    
    setIrregularPaymentStream({
      ...irregularPaymentStream,
      startDate: newStartDate,
      endDate: newEndDate
    });
  };

  /**
   * Handler for when the count (number of payments) is changed.
   * Updates both the count and recalculates the end date.
   */
  const handleCountChange = (newCount: number) => {
    const newEndDate = calculateEndDate(irregularPaymentStream.startDate, newCount);
    
    setIrregularPaymentStream({
      ...irregularPaymentStream,
      count: newCount,
      endDate: newEndDate
    });
  };

  /**
   * Checks if two date ranges overlap.
   * Two ranges overlap if: startA <= endB AND endA >= startB
   */
  const doDateRangesOverlap = (
    startA: Date | null,
    endA: Date | null,
    startB: Date | null,
    endB: Date | null
  ): boolean => {
    if (!startA || !endA || !startB || !endB) return false;
    
    const startATime = new Date(startA).setHours(0, 0, 0, 0);
    const endATime = new Date(endA).setHours(0, 0, 0, 0);
    const startBTime = new Date(startB).setHours(0, 0, 0, 0);
    const endBTime = new Date(endB).setHours(0, 0, 0, 0);
    
    return startATime <= endBTime && endATime >= startBTime;
  };

  /**
   * Validates that the current irregular payment stream doesn't overlap with existing ones.
   */
  const checkForOverlappingPaymentStreams = (): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];
    
    if (!irregularPaymentStream.startDate || !irregularPaymentStream.endDate) {
      return errors;
    }

    // Get all other irregular payment streams (excluding the one being edited)
    const otherPaymentStreams = financeQuoteIrregularPaymentStreams.filter(
      stream => stream.id !== irregularPaymentStream.id
    );

    for (const existingStream of otherPaymentStreams) {
      if (doDateRangesOverlap(
        irregularPaymentStream.startDate,
        irregularPaymentStream.endDate,
        existingStream.startDate,
        existingStream.endDate
      )) {
        const existingStartDate = existingStream.startDate 
          ? new Date(existingStream.startDate).toLocaleDateString('en-US') 
          : 'N/A';
        const existingEndDate = existingStream.endDate 
          ? new Date(existingStream.endDate).toLocaleDateString('en-US') 
          : 'N/A';
        
        errors.push({
          message: `This payment stream overlaps with an existing stream (${existingStartDate} - ${existingEndDate})`,
          canBeBypassed: false
        });
        break; // Only show one overlap error
      }
    }

    return errors;
  };

  const onSubmit = (bypassTheErrorsThatCanBeBypassed: boolean, actionType: GenericEditorActionType) => {
    const errors: IGenericItemEditorError[] = [];
    const dateErrors = datesValidator();
    errors.push(...dateErrors);
    
    // Check for overlapping payment streams
    const overlapErrors = checkForOverlappingPaymentStreams();
    errors.push(...overlapErrors);
    
    if (!hasValue(irregularPaymentStream.startDate)) {
      errors.push({message: "Start Date is a required field", canBeBypassed: false});
    }
    
    if (irregularPaymentStream.amount <= 0) {
      errors.push({message: "The amount has to be greater than 0", canBeBypassed: false})
    }
    
    if (irregularPaymentStream.count <= 0) {
      errors.push({message: "The number of irregular payments has to be greater than 0", canBeBypassed: false})
    }

    setValidatorErrors(errors);
    
    const thereAreErrorsThatCanNotBeBypassed = errors.filter(e => !e.canBeBypassed).length > 0;
    const thereAreErrorsThatCanBeBypassed = errors.filter(e => e.canBeBypassed).length > 0;

    // It returns early IF 
    // 1. There are any errors that CAN'T be bypassed
    // 2. There are errors that CAN be bypassed, but the user didn't click the "Bypass" button
    if (thereAreErrorsThatCanNotBeBypassed || (thereAreErrorsThatCanBeBypassed && !bypassTheErrorsThatCanBeBypassed)) {
      return;
    }

    if (props.onSubmit) {
      props.onSubmit(irregularPaymentStream);
    }
  }

  // Checks to make sure that the user didn't input "23" as the year. This will prompt them to input "2023" as the year.
  const datesValidator = (): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];
    
    if (hasValue(irregularPaymentStream.startDate) && new Date(irregularPaymentStream.startDate!).getFullYear() < 1000) {
      errors.push({message: "The start date's year has to have 4 digits. Ex- instead of '23', enter '2023'.", canBeBypassed: false});
    }

    return errors;
  }
  
  return (
    <div className={styles.financeIrregularPaymentStreamEditorDialogContainer}>
      <DialogTitle id="form-dialog-title">
        {props.title}
      </DialogTitle>
      <DialogContent>
        <div style={{display: 'flex', flexWrap: "wrap", flexDirection: 'column', padding: '10px', flex: 1}}>
          <ComboBox
            label={"Start Date"}
            data={paymentDateOptions}
            textField="label"
            dataItemKey="label"
            value={selectedPaymentDateOption}
            onChange={(event: ComboBoxChangeEvent) => {
              const selectedOption = event.value as IrregularPaymentStartDateOption | null;
              handleStartDateChange(selectedOption);
            }}
            style={{width: '100%'}}
            required={true}
          />
          <Input
            style={{width: '100%', marginLeft: '3px'}}
            valid={irregularPaymentStream.count > 0}
            label={'Number of Payments'}
            value={irregularPaymentStream.count}
            type='number'
            onChange={(e: InputChangeEvent) => handleCountChange(Number(e.target.value))}
            step={1}
            min={1}
            max={6}
          />
          <NoScrollNumericTextBox
            style={{width: '100%', marginRight: '3px'}}
            label={'Amount'}
            value={irregularPaymentStream.amount}
            format="c2"
            spinners={false}
            onChange={(e: NumericTextBoxChangeEvent) => {
              const value = e.target.value;
              setIrregularPaymentStream({...irregularPaymentStream, amount: value ?? 0});
            }}
            required={true}
            step={50}
            min={0}
          />
        {errorsThatCanNotBeBypassed.length > 0 &&
          <Grid item xs={12}>
            {errorsThatCanNotBeBypassed.map((error, key) => <Error key={key}>{error.message}</Error>)}
          </Grid>
        }
        {errorsThatCanBeBypassed.length > 0 &&
          <Grid item xs={12}>
            {errorsThatCanBeBypassed.map((error, key) => <Hint key={key}>{error.message}</Hint>)}
          </Grid>
        }
        </div>
        

        <DialogActions>
          {errorsThatCanBeBypassed.length > 0 && errorsThatCanNotBeBypassed.length === 0 &&
            <Button
              onClick={() => onSubmit(true, "Bypass")}
              color="error"
              variant="contained"
            >
              Bypass
            </Button>
          }
          <Button
            onClick={() => onSubmit(false, props.submitButtonText)}
            color="primary"
            variant="contained"
          >
            {props.submitButtonText}
          </Button>
          {thereWasAtLeastOneIrregularPaymentOnOpen &&
            <Button
              onClick={() => props.onRemoveSingle(irregularPaymentStream.id)}
              color="error"
              variant="contained"
            >
              Remove
            </Button>
          }
          <Button
            autoFocus
            onClick={props.closeDialog}
            color="secondary"
            variant="contained"
          >
            Cancel
          </Button>
        </DialogActions>
      </DialogContent>
    </div>
  );
}
